import { createTheme, Paper, ThemeProvider } from '@mui/material'
import HFCard from '../../../components/HFCard'
import { selectChildPractices, selectGroups } from '../../../redux/customer'
import { useAppSelector } from '../../../redux/store'
import { locationNoun } from '../../practice/settings/constants'
import Filters from './Filters'
import { DataGrid } from '@mui/x-data-grid'
import CompliancePlip from '../../../components/CompliancePlip'
import { useMemo, useState } from 'react'
import { ChildPracticeMetadata, Groups } from '../../../types'
import { ComplianceFilterOption } from './types'
import { complianceSortComparator } from '../../../helpers/complianceSortComparator'
import { Link } from 'react-router-dom'

const tableTheme = createTheme({
  typography: {
    fontFamily: '"Open Sans", sans-serif',
    fontSize: 13
  },
  components: {
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: "bold"
        }
      }
    }
  }
})

const Practices = () => {
  const childPractices = useAppSelector(selectChildPractices)
  const groups = useAppSelector(selectGroups)

  const [search, setSearch] = useState('')
  const [selectedGroups, setSelectedGroups] = useState([] as Groups[])
  const [selectedCompliance, setSelectedCompliance] = useState([] as ComplianceFilterOption[])

  const onChangeFilter = (field: string, value: ComplianceFilterOption[] | Groups[] | string) => {
    switch (field) {
      case 'search':
        setSearch(value as string)
        break
      case 'groups':
        setSelectedGroups(value as Groups[])
        break
      case 'compliance':
        setSelectedCompliance(value as ComplianceFilterOption[])
        break
    }
  }

  const filteredPractices: ChildPracticeMetadata[] = useMemo(() => {
    if (!childPractices?.length) return []

    const formattedSearch = search?.trim().toLowerCase() || ''

    const groupMembers = selectedGroups.flatMap((group) => group?.members.map((member) => member.customer_number));

    return childPractices.filter(practice => {
      const matchesSearch =
        !formattedSearch ||
        practice.customer_number?.includes(formattedSearch) ||
        practice.name?.toLowerCase().includes(formattedSearch) ||
        practice.city?.toLowerCase().includes(formattedSearch) ||
        practice.bio_message_short?.includes(formattedSearch) ||
        practice.kit_message_short?.includes(formattedSearch) ||
        practice.mwaste_message_short?.includes(formattedSearch) ||
        practice.amalgam_message_short?.includes(formattedSearch) ||
        practice.waterline_message_short?.includes(formattedSearch)

      if (!matchesSearch) return false

      if (selectedGroups.length && !groupMembers.includes(practice.customer_number)) return false

      return selectedCompliance.every(
        compliance =>
          practice[compliance.field as keyof ChildPracticeMetadata] === compliance.value
      )
    })
  }, [search, selectedGroups, selectedCompliance, childPractices, groups])

  const clearFilters = () => {
    setSelectedCompliance([])
    setSelectedGroups([])
  }

  return (
    <HFCard headerText={`Your ${locationNoun}s`}>
      <div className='card-body'>
        <Filters onChangeFilter={onChangeFilter} onClearFilters={clearFilters} />
        <Paper>
          <ThemeProvider theme={tableTheme}>
            <DataGrid
              sx={{ borderLeft: "none", borderRight: "none", borderBottom: "none", borderRadius: "0" }}
              rows={filteredPractices ?? []}
              getRowId={(row) => row.customer_number}
              columns={[
                {
                  field: "status",
                  headerName: "Status",
                  sortable: true,
                  width: 90,
                  renderCell: (params) => (
                    <CompliancePlip message='' status={params.row.status} />
                  ),
                  align: "center",
                  getSortComparator: (sortDirection) => {
                    return (a, b, cellParams1, cellParams2) => complianceSortComparator(sortDirection, a, b, cellParams1, cellParams2)
                  }
                },
                {
                  field: "name",
                  headerName: "Name",
                  flex: 1,
                  minWidth: 450,
                  renderCell: (params) => (
                    <Link
                      to={`/corp/practice/${params.row.customer_number}/dashboard`}
                      style={{ color: '#19a9d5', textDecoration: 'none'}}
                    >
                      {params.row.name.trim()} ({params.row.customer_number})
                    </Link>
                  )
                },
                {
                  field: "location",
                  headerName: "Location",
                  flex: 1,
                  minWidth: 180,
                  valueGetter: (value, row) => {
                    return `${row.city}, ${row.state}`
                  },
                },
                {
                  field: "osha_hipaa_compliance_status",
                  headerName: "OSHA/HIPAA",
                  sortable: true,
                  flex: 1,
                  width: 100,
                  renderCell: (params) => (
                    <Link to={`/corp/practice/${params.row.customer_number}/osha-hipaa`}>
                      <CompliancePlip message={params.row.osha_hipaa_message_short} status={params.row.osha_hipaa_compliance_status} />
                    </Link>
                  ),
                  align: "center",
                  getSortComparator: (sortDirection) => {
                    return (a, b, cellParams1, cellParams2) => complianceSortComparator(sortDirection, a, b, cellParams1, cellParams2)
                  }
                },
                {
                  field: "training_compliance_status",
                  headerName: "Training",
                  sortable: true,
                  flex: 1,
                  width: 80,
                  renderCell: (params) => (
                    <Link to={`/corp/practice/${params.row.customer_number}/training`}>
                      <CompliancePlip message={params.row.training_message_short} status={params.row.training_compliance_status} />
                    </Link>
                  ),
                  align: "center",
                  getSortComparator: (sortDirection) => {
                    return (a, b, cellParams1, cellParams2) => complianceSortComparator(sortDirection, a, b, cellParams1, cellParams2)
                  }
                },
                {
                  field: "kit_compliance_status",
                  headerName: "Medications",
                  sortable: true,
                  flex: 1,
                  width: 100,
                  renderCell: (params) => (
                    <Link to={`/corp/practice/${params.row.customer_number}/medications`}>
                      <CompliancePlip message={params.row.kit_message_short} status={params.row.kit_compliance_status} />
                    </Link>
                  ),
                  align: "center",
                  getSortComparator: (sortDirection) => {
                    return (a, b, cellParams1, cellParams2) => complianceSortComparator(sortDirection, a, b, cellParams1, cellParams2)
                  }
                },
                {
                  field: "device_compliance_status",
                  headerName: "Devices",
                  sortable: true,
                  flex: 1,
                  width: 80,
                  renderCell: (params) => (
                    <Link to={`/corp/practice/${params.row.customer_number}/devices`}>
                      <CompliancePlip message={params.row.device_message_short} status={params.row.device_compliance_status} />
                    </Link>
                  ),
                  align: "center",
                  getSortComparator: (sortDirection) => {
                    return (a, b, cellParams1, cellParams2) => complianceSortComparator(sortDirection, a, b, cellParams1, cellParams2)
                  }
                },
                {
                  field: "bio_compliance_status",
                  headerName: "Biological",
                  sortable: true,
                  flex: 1,
                  width: 90,
                  renderCell: (params) => (
                    <Link to={`/corp/practice/${params.row.customer_number}/biological`}>
                      <CompliancePlip message={params.row.bio_message_short} status={params.row.bio_compliance_status} />
                    </Link>
                  ),
                  align: "center",
                  getSortComparator: (sortDirection) => {
                    return (a, b, cellParams1, cellParams2) => complianceSortComparator(sortDirection, a, b, cellParams1, cellParams2)
                  }
                },
                {
                  field: "waterline_compliance_status",
                  headerName: "Waterline",
                  sortable: true,
                  flex: 1,
                  width: 90,
                  renderCell: (params) => (
                    <Link to={`/corp/practice/${params.row.customer_number}/waterline-management`}>
                      <CompliancePlip message={params.row.waterline_message_short} status={params.row.waterline_compliance_status} />
                    </Link>
                  ),
                  align: "center",
                  getSortComparator: (sortDirection) => {
                    return (a, b, cellParams1, cellParams2) => complianceSortComparator(sortDirection, a, b, cellParams1, cellParams2)
                  }
                },
                {
                  field: "mwaste_compliance_status",
                  headerName: "Medical Waste",
                  sortable: true,
                  flex: 1,
                  width: 120,
                  renderCell: (params) => (
                    <Link to={`/corp/practice/${params.row.customer_number}/medical-waste`}>
                      <CompliancePlip message={params.row.mwaste_message_short} status={params.row.mwaste_compliance_status} />
                    </Link>
                  ),
                  align: "center",
                  getSortComparator: (sortDirection) => {
                    return (a, b, cellParams1, cellParams2) => complianceSortComparator(sortDirection, a, b, cellParams1, cellParams2)
                  }
                },
                {
                  field: "amalgam_compliance_status",
                  headerName: "Amalgam",
                  sortable: true,
                  flex: 1,
                  width: 90,
                  renderCell: (params) => (
                    <Link to={`/corp/practice/${params.row.customer_number}/amalgam-waste`}>
                      <CompliancePlip message={params.row.amalgam_message_short} status={params.row.amalgam_compliance_status} />
                    </Link>
                  ),
                  align: "center",
                  getSortComparator: (sortDirection) => {
                    return (a, b, cellParams1, cellParams2) => complianceSortComparator(sortDirection, a, b, cellParams1, cellParams2)
                  }
                },
              ]}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 25,
                  },
                }
              }}
              pageSizeOptions={[5, 10, 20, 25, 50, 100]}
              loading={!childPractices.length}
              autoHeight
              density="compact"
            />
          </ThemeProvider>
        </Paper>
      </div>
    </HFCard>
  )
}

export default Practices